'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FormData {
  name: string;
  email: string;
  phone: string;
  serviceType: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  serviceType?: string;
}

const serviceTypes = [
  'Patio Cleaning',
  'Pool Deck Cleaning',
  'Driveway Cleaning',
  'Paver Restoration',
  'Furniture Cleaning',
  'Commercial Services',
  'Other'
];

const LeadCaptureSection = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    serviceType: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData.serviceType) {
      newErrors.serviceType = 'Please select a service type';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        serviceType: ''
      });

      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
    }, 1500);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  if (!isHydrated) {
    return (
      <section className="py-16 md:py-20 bg-primary">
        <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="animate-pulse space-y-6">
            <div className="h-10 bg-primary-foreground/20 rounded w-3/4 mx-auto"></div>
            <div className="h-6 bg-primary-foreground/20 rounded w-full mx-auto"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="h-12 bg-primary-foreground/20 rounded"></div>
              <div className="h-12 bg-primary-foreground/20 rounded"></div>
              <div className="h-12 bg-primary-foreground/20 rounded"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 md:py-20 bg-primary relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-64 h-64 bg-accent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-10">
          <h2 className="font-headline font-bold text-3xl md:text-4xl lg:text-5xl text-primary-foreground mb-4">
            Get Your Free Quote Today
          </h2>
          <p className="font-body text-lg md:text-xl text-primary-foreground/90 max-w-3xl mx-auto">
            Fill out the form below and we'll contact you within 2 hours to discuss your patio cleaning needs
          </p>
        </div>

        {submitSuccess && (
          <div className="mb-8 max-w-2xl mx-auto p-4 bg-success/20 border border-success/50 rounded-md flex items-start space-x-3">
            <Icon name="CheckCircleIcon" size={24} className="text-success flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-body font-semibold text-primary-foreground mb-1">Request Received!</h3>
              <p className="font-body text-sm text-primary-foreground/80">
                Thank you for your interest. We'll contact you within 2 hours to provide your free quote.
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            <div>
              <label htmlFor="lead-name" className="block font-body font-medium text-primary-foreground mb-2">
                Your Name *
              </label>
              <input
                type="text"
                id="lead-name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-md font-body focus:outline-none focus:ring-2 focus:ring-accent transition-brand bg-card ${
                  errors.name ? 'border-error' : 'border-input'
                }`}
                placeholder="John Doe"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-error font-body">{errors.name}</p>
              )}
            </div>

            <div>
              <label htmlFor="lead-email" className="block font-body font-medium text-primary-foreground mb-2">
                Email Address *
              </label>
              <input
                type="email"
                id="lead-email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-md font-body focus:outline-none focus:ring-2 focus:ring-accent transition-brand bg-card ${
                  errors.email ? 'border-error' : 'border-input'
                }`}
                placeholder="john@example.com"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-error font-body">{errors.email}</p>
              )}
            </div>

            <div>
              <label htmlFor="lead-phone" className="block font-body font-medium text-primary-foreground mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                id="lead-phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-md font-body focus:outline-none focus:ring-2 focus:ring-accent transition-brand bg-card ${
                  errors.phone ? 'border-error' : 'border-input'
                }`}
                placeholder="(602) 777-3005"
              />
              {errors.phone && (
                <p className="mt-1 text-sm text-error font-body">{errors.phone}</p>
              )}
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <div>
              <label htmlFor="lead-serviceType" className="block font-body font-medium text-primary-foreground mb-2">
                Service Interested In *
              </label>
              <select
                id="lead-serviceType"
                name="serviceType"
                value={formData.serviceType}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-md font-body focus:outline-none focus:ring-2 focus:ring-accent transition-brand bg-card ${
                  errors.serviceType ? 'border-error' : 'border-input'
                }`}
              >
                <option value="">Select a service</option>
                {serviceTypes.map((service) => (
                  <option key={service} value={service}>
                    {service}
                  </option>
                ))}
              </select>
              {errors.serviceType && (
                <p className="mt-1 text-sm text-error font-body">{errors.serviceType}</p>
              )}
            </div>

            <div className="flex items-end">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-accent text-accent-foreground px-8 py-3 rounded-md font-body font-semibold hover:bg-accent/90 transition-brand shadow-cta-glow hover:shadow-cta-glow-hover disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {isSubmitting ? (
                  <>
                    <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <Icon name="PaperAirplaneIcon" size={20} />
                    <span>Get Free Quote</span>
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-center space-x-6 text-primary-foreground/80">
            <div className="flex items-center space-x-2">
              <Icon name="CheckCircleIcon" size={18} className="text-success" />
              <span className="font-body text-sm">No Obligation</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="CheckCircleIcon" size={18} className="text-success" />
              <span className="font-body text-sm">2 Hour Response</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="CheckCircleIcon" size={18} className="text-success" />
              <span className="font-body text-sm">Free Consultation</span>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
};

export default LeadCaptureSection;